import UILabel from "components/ui/UILabel";
import React from "react";
import { formatDateString } from "utils/date";
import StepNode from "./StepNode";
import * as Styled from "./styled";
import {
  TimelineEvent,
  TimelineEventStatus,
  TimelineEventStepStatus,
} from "./types";
import { getEventStatus } from "./utils";

export { TimelineEventStatus, TimelineEventStepStatus };

interface Props {
  events: TimelineEvent[];
}

const Timeline: React.FC<Props> = ({ events }) => (
  <Styled.Wrapper>
    {events.map((event, eventIndex) => {
      const status = getEventStatus({
        date: event.date,
        nextDate: events[eventIndex + 1]?.date,
        prevDate: events[eventIndex - 1]?.date,
        index: eventIndex,
      });
      const isToday = status === TimelineEventStatus.today;
      const data = event.data(status);
      return (
        <Styled.Event key={eventIndex} $status={status}>
          {eventIndex < events.length - 1 && (
            <Styled.EventConnector>
              <svg viewBox="0 0 6 10" preserveAspectRatio="none">
                <title>Connector</title>
                <line
                  x1={3}
                  x2={3}
                  y1={0}
                  y2={10}
                  strokeWidth={data.steps?.length ? 2 : 6}
                />
              </svg>
            </Styled.EventConnector>
          )}

          <Styled.EventMain
            $hasSteps={data.steps?.length > 0}
            $stepStatus={data.steps?.[0].status}
          >
            <Styled.EventNode />

            <Styled.EventText>
              {event.date && (
                <Styled.EventDate bold={isToday} color={isToday && "primary"}>
                  {isToday
                    ? "Today"
                    : formatDateString(event.date, "textShortWithWeekday")}
                </Styled.EventDate>
              )}
              <UILabel.XLarge bold>{data.title}</UILabel.XLarge>
              {data.description && (
                <Styled.EventDescription color="textBody">
                  {data.description}
                </Styled.EventDescription>
              )}
            </Styled.EventText>
          </Styled.EventMain>

          {data.steps?.length && (
            <Styled.EventSteps>
              {data.steps.map((step, stepIndex) => (
                <Styled.EventStep
                  key={stepIndex}
                  $status={step.status}
                  $nextStatus={data.steps[stepIndex + 1]?.status}
                >
                  <Styled.EventStepMain>
                    <StepNode status={step.status}>{stepIndex + 1}</StepNode>

                    <Styled.EventStepContent>
                      <Styled.EventStepLineItem>
                        <Styled.EventStepText>
                          <UILabel.Medium bold>{step.title}</UILabel.Medium>
                          {step.subtitle && (
                            <UILabel.Micro color="disabled" caps bold>
                              {step.subtitle}
                            </UILabel.Micro>
                          )}
                        </Styled.EventStepText>

                        {step.detail && (
                          <Styled.EventStepDetail>
                            <UILabel.Large bold color="primary">
                              {step.detail}
                            </UILabel.Large>
                          </Styled.EventStepDetail>
                        )}
                      </Styled.EventStepLineItem>

                      {step.lineItems?.map((lineItem, lineItemIndex) => (
                        <Styled.EventStepLineItem key={lineItemIndex}>
                          <Styled.EventStepText>
                            <UILabel.Medium>{lineItem.label}</UILabel.Medium>
                          </Styled.EventStepText>

                          <Styled.EventStepDetail>
                            <UILabel.Large bold color="primary">
                              {lineItem.amount}
                            </UILabel.Large>
                          </Styled.EventStepDetail>
                        </Styled.EventStepLineItem>
                      ))}
                    </Styled.EventStepContent>
                  </Styled.EventStepMain>
                </Styled.EventStep>
              ))}
            </Styled.EventSteps>
          )}
        </Styled.Event>
      );
    })}
  </Styled.Wrapper>
);

export default Timeline;
